import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { isEqual } from 'es-toolkit';
import {
  FOOTER_NOTE_MAX_LENGTH,
  TERMS_MAX_LENGTH,
  type OutputDocumentType
} from '@insio/shared-types';
import {
  DocumentOutputSettingsDocument,
  UpdateDocumentOutputSettingsDocument,
  type LocalizedText,
  type UpdateDocumentOutputSettingsInput
} from '@/graphql';

/** The languages a generated document is printed in. */
export const OUTPUT_LOCALES = ['cs', 'en'] as const;
export type OutputLocale = (typeof OUTPUT_LOCALES)[number];

/** The texts one settings row holds, each in every output language. */
export const OUTPUT_TEXT_FIELDS = ['terms', 'footerNote'] as const;
export type OutputTextField = (typeof OUTPUT_TEXT_FIELDS)[number];

/** The longest text per language the backend stores for each field. */
export const OUTPUT_TEXT_MAX_LENGTH: Readonly<Record<OutputTextField, number>> = {
  terms: TERMS_MAX_LENGTH,
  footerNote: FOOTER_NOTE_MAX_LENGTH
};

/** Visible rows per field: the terms run long, the footer note is a line or two. */
export const OUTPUT_TEXT_ROWS: Readonly<Record<OutputTextField, number>> = {
  terms: 12,
  footerNote: 4
};

/** One text in every output language; blank where it is not set. */
export type LocalizedDraft = Readonly<Record<OutputLocale, string>>;
type Draft = Readonly<Record<OutputTextField, LocalizedDraft>>;

const EMPTY_TEXT: LocalizedDraft = { cs: '', en: '' };

/** The stored text as the fields edit it — unset languages read as blank. */
const draftOf = (text: Pick<LocalizedText, OutputLocale> | undefined): LocalizedDraft => ({
  cs: text?.cs ?? '',
  en: text?.en ?? ''
});

const isBlank = (text: string): boolean => text.trim() === '';

const OTHER_LOCALE: Readonly<Record<OutputLocale, OutputLocale>> = { cs: 'en', en: 'cs' };

/** The other language's text, printed for a language left blank. */
export interface PrintedFallback {
  readonly locale: OutputLocale;
  readonly text: string;
}

/**
 * What the PDF prints for `locale` when it is left blank, in the backend's order: the text in
 * that language, else the text in the other one. Null when the language has its own text.
 */
export function printedFallback(
  value: LocalizedDraft,
  locale: OutputLocale
): PrintedFallback | null {
  if (!isBlank(value[locale])) return null;
  const other = OTHER_LOCALE[locale];
  return isBlank(value[other]) ? null : { locale: other, text: value[other].trim() };
}

/** A blank language is sent as null; a field with no language left is cleared outright. */
const inputOf = (text: LocalizedDraft): { cs: string | null; en: string | null } | null => {
  const cs = isBlank(text.cs) ? null : text.cs;
  const en = isBlank(text.en) ? null : text.en;
  return cs === null && en === null ? null : { cs, en };
};

/**
 * One document type's output settings: the stored row, a draft of its texts, and saving it.
 * Only the fields that changed are sent, each with both languages, as the backend replaces them.
 */
export function useDocumentOutputSection(documentType: OutputDocumentType) {
  const { data, loading, error } = useQuery(DocumentOutputSettingsDocument, {
    variables: { documentType }
  });
  // The row carries its id, so the response lands in the cache and the query reads it back.
  const [updateSettings, { loading: saving }] = useMutation(UpdateDocumentOutputSettingsDocument);
  const [edits, setEdits] = useState<Partial<Draft>>({});

  const settings = data?.documentOutputSettings;
  const stored: Draft = {
    terms: draftOf(settings?.terms),
    footerNote: draftOf(settings?.footerNote)
  };
  const values: Draft = {
    terms: edits.terms ?? stored.terms,
    footerNote: edits.footerNote ?? stored.footerNote
  };

  const changedFields = settings
    ? OUTPUT_TEXT_FIELDS.filter(field => !isEqual(values[field], stored[field]))
    : [];
  const isTooLong = (field: OutputTextField, locale: OutputLocale): boolean =>
    values[field][locale].length > OUTPUT_TEXT_MAX_LENGTH[field];
  const hasTooLong = OUTPUT_TEXT_FIELDS.some(field =>
    OUTPUT_LOCALES.some(locale => isTooLong(field, locale))
  );
  const canSave = changedFields.length > 0 && !hasTooLong && !saving;

  const setText = (field: OutputTextField, locale: OutputLocale, text: string) => {
    setEdits(current => ({
      ...current,
      [field]: { ...(current[field] ?? stored[field] ?? EMPTY_TEXT), [locale]: text }
    }));
  };

  /** Stores the changed texts; false when the backend refused them, which keeps the draft. */
  const save = async (): Promise<boolean> => {
    const input: UpdateDocumentOutputSettingsInput = {
      ...(changedFields.includes('terms') && { terms: inputOf(values.terms) }),
      ...(changedFields.includes('footerNote') && { footerNote: inputOf(values.footerNote) })
    };
    try {
      // errorPolicy 'none' (vs global 'all') makes GraphQL errors throw into the catch below
      await updateSettings({ variables: { documentType, input }, errorPolicy: 'none' });
      setEdits({});
      return true;
    } catch {
      return false;
    }
  };

  return { settings, loading, error, values, isTooLong, canSave, saving, setText, save };
}
